import config from '@config';
import state from '@state';
import { showLoading, displayResponse } from '@response/renderer';
import { log } from '@utils/logger';
import { v4 as uuidv4 } from 'uuid';
import i18n from '@i18n';
import { smartFetch } from '@utils/smartFetch';

/**
 * Sends the query to the webhook and processes the result.
 *
 * @param query - The search string from the user.
 * @param inputType - The type of input triggering the query ('text' | 'voice'). Defaults to 'text'.
 * @param skipLoading - Whether to skip showing the loading UI. Defaults to `false`.
 * @returns A promise that resolves when the query is processed.
 */
// export async function processQuery(
// 	query: string,
// 	inputType: 'text' | 'voice' = 'text',
// 	skipLoading: boolean = false,
// ): Promise<void> {
// 	if (!query) {
// 		console.warn(i18n.t('error.emptyQuery', { prefix: i18n.t('log.prefix') }));
// 		return;
// 	}

// 	const queryId = uuidv4();

// 	try {
// 		// Cancel any existing query
// 		state.abortManager.cancelCurrentRequest();

// 		// Stop any existing initAudioRecording
// 		if (state.currentAudio) {
// 			state.currentAudio.pause();
// 			state.currentAudio = null;
// 		}

// 		// Create a new abort signal for this query
// 		const signal = state.abortManager.createAbortSignal();
// 		state.activeQueryId = queryId;

// 		if (!skipLoading) {
// 			showLoading();
// 		}

// 		const webhookUrl = state.currentInstance?.webhookUrl || config.webhookUrl;

// 		const fd = new FormData();
// 		fd.append('query', query);

// 		const response = await smartFetch(webhookUrl, {
// 			method: 'POST',
// 			body: fd,
// 			signal: signal,
// 		});

// 		if (state.activeQueryId !== queryId) {
// 			log(i18n.t('log.querySuperceeded'));
// 			return;
// 		}

// 		if (!response.ok) {
// 			throw new Error(`${i18n.t('error.HTTPError')}: ${response.status}`);
// 		}

// 		const data = await response.json();

// 		if (state.activeQueryId === queryId) {
// 			displayResponse(data, inputType);
// 		}
// 	} catch (error: any) {
// 		if (error.name !== 'AbortError' && state.activeQueryId === queryId) {
// 			console.error(i18n.t('error.processingError', { prefix: i18n.t('log.prefix') }), error);
// 			displayResponse(
// 				{
// 					error: i18n.t('error.processingErrorMessage'),
// 				},
// 				inputType,
// 			);
// 		}
// 	}
// }

export function precheckAndSetup(
	query: string,
	inputType: 'text' | 'voice',
	skipLoading: boolean,
) {

	// Verify there is a query to process.
	if (!query || inputType !== 'text') {
		console.warn(i18n.t('error.emptyQuery', { prefix: i18n.t('log.prefix') }));
		return null;
	}

	// Generate a UUID query ID.
	const queryId = uuidv4();

	// Cancel any existing query.
	state.abortManager.cancelCurrentRequest();

	// Stop any existing initAudioRecording.
	if (state.currentAudio) {
		state.currentAudio.pause();
		state.currentAudio = null;
	}

	// Create a new abort signal for the upcoming query.
	const signal = state.abortManager.createAbortSignal();
	state.activeQueryId = queryId;

	if (!skipLoading) showLoading();

	// Return
	return {
		queryId,
		signal,
	};
}

export async function wait(ms: number) {
	return await new Promise(resolve => setTimeout(resolve, ms));
}

export function processQuery(
	query: string,
	inputType: 'text' | 'voice' = 'text',
	skipLoading: boolean = false,
): Promise<void> {
	showLoading();
	/* eslint-disable-next-line no-async-promise-executor */
	return new Promise(async resolve => {
		// Execute and return precheck and setup results.
		const result = precheckAndSetup(query, inputType, skipLoading);
		if (result === null) return;
		const { queryId, signal } = result;
		let hasResult = false;

		try {

			// Define the URL.
			const webhookUrl = state.currentInstance?.webhookUrl || config.webhookUrl;
			let pollingUrl = config.pollingUrl;

			// Create the form data.
			const fd = new FormData();
			fd.append('query', query);

			// Create the query.
			const response = await smartFetch(webhookUrl, {
				method: 'POST',
				body: fd,
				signal: signal,
			});

			// Check if the query was superceded while waiting for the response.
			if (state.activeQueryId !== queryId) {
				log(i18n.t('log.querySuperceeded'));
				return;
			}

			// Handle non-OK responses.
			if (!response.ok) throw new Error(`${i18n.t('error.HTTPError')}: ${response.status}`);

			// Get the task ID.
			const results = await response.json();
			pollingUrl = pollingUrl.replace('{taskId}', results.id);

			// Define the count.
			let count = 0;

			// Max wait: 2 minutes.
			while (count <= 24) {
				const pResponse = await smartFetch(pollingUrl, {
					method: 'GET',
					signal: signal,
				});

				if (state.activeQueryId !== queryId) {
					log(i18n.t('log.querySuperceeded'));
					return;
				}

				if (!pResponse.ok) throw new Error(`${i18n.t('error.HTTPError')}: ${pResponse.status}`);

				const pResults = await pResponse.json();
				if (pResults.status === 'pending') {
					count++;
					await wait(5_000);
					continue;
				}

				hasResult = true;
				if (state.activeQueryId === queryId) displayResponse(pResults, inputType);
				break;
			}

			// If we got here, and don't have a result, we can assume it timed out.
			if (!hasResult) throw new Error(i18n.t('error.requestIssue'));

		} catch (err: any) {
			if (err.name !== 'AbortError' && state.activeQueryId === queryId) {
				console.error(i18n.t('error.processingError', { prefix: i18n.t('log.prefix') }), err);
				displayResponse(
					{
						error: i18n.t('error.processingErrorMessage'),
					},
					inputType,
				);
			}
		} finally {
			resolve();
		}
	});
}
